import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, MapPin, ShoppingBag, Ban, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  useListingDetail,
  usePurchaseListing,
  useCancelListing,
} from "@/lib/market";

// Phase 22a — ListingDetailModal.
// Shows a listing's full detail, buy CTA or cancel-listing CTA depending on
// ownership. On successful buy, the modal transitions to a confirmed state
// showing the suggested Safe Zones.

export default function ListingDetailModal({ id, onClose }) {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const detail = useListingDetail(id);
  const purchase = usePurchaseListing();
  const cancel = useCancelListing();
  const [photoIdx, setPhotoIdx] = useState(0);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setPhotoIdx(0);
    setPurchaseResult(null);
    setError("");
  }, [id]);

  const listing = detail.data;
  const isSeller = !!listing && listing.seller_id === user?.id;
  const isSold = listing && listing.status === "sold";
  const isCancelled = listing && listing.status === "cancelled";
  const isExpired = listing && listing.status === "expired";
  const balance = user?.figutokens_balance ?? 0;
  const canAfford = listing ? balance >= (listing.price || 0) : false;

  const onBuy = useCallback(async () => {
    if (!listing) return;
    setError("");
    try {
      const r = await purchase.mutateAsync({ id: listing.id });
      setPurchaseResult(r);
      toast.success(t("market.purchase_success", "Purchase confirmed."));
      if (typeof refresh === "function") {
        try {
          await refresh();
        } catch (_) {}
      }
    } catch (err) {
      const data = err?.response?.data?.detail;
      const code =
        data && typeof data === "object" ? data.error_code : null;
      let msg =
        (data && typeof data === "object" && data.message) || err.message;
      if (code === "insufficient_balance") {
        msg = t(
          "market.err_insufficient",
          "Not enough FiguTokens — top up via the Wallet."
        );
      } else if (code === "listing_no_longer_available") {
        msg = t("market.err_already_sold", "This listing is no longer available.");
      } else if (code === "cannot_purchase_own_listing") {
        msg = t("market.err_self_purchase", "You cannot buy your own listing.");
      }
      setError(msg);
    }
  }, [listing, purchase, refresh, t]);

  const onCancel = useCallback(async () => {
    if (!listing) return;
    setError("");
    try {
      await cancel.mutateAsync(listing.id);
      toast.success(t("market.cancel_success", "Listing cancelled."));
      onClose();
    } catch (err) {
      const data = err?.response?.data?.detail;
      const msg =
        (data && typeof data === "object" && data.message) || err.message;
      setError(msg);
    }
  }, [listing, cancel, onClose, t]);

  if (!id) return null;

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid="listing-detail-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] max-h-[92vh] bg-[#0A0A0A] border border-white/10 rounded-xl shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)] flex flex-col overflow-hidden"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2
            data-testid="listing-detail-title"
            className="font-display text-lg font-semibold text-white"
          >
            {listing ? `#${listing.number}` : t("market.loading", "Loading…")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="listing-detail-close"
            aria-label="Close"
            className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-white/5 text-white/70 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {detail.isLoading ? (
            <div className="flex items-center justify-center py-16 text-white/60">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !listing ? (
            <div className="p-8 text-center text-sm text-[#A1A1AA] font-body">
              {t("market.not_found", "Listing not available.")}
            </div>
          ) : purchaseResult ? (
            // ── Success state ──────────────────────────────────────
            <div
              data-testid="listing-purchase-confirmed"
              className="px-6 py-6 space-y-4"
            >
              <p className="text-lg font-display font-semibold text-[#E5B83A]">
                {t("market.purchase_confirmed_title", "Purchase confirmed")}
              </p>
              <p className="text-sm text-white/80 font-body">
                {t(
                  "market.purchase_confirmed_body",
                  "You paid {price} FiguTokens. Coordinate meetup via chat and the suggested Safe Zone."
                ).replace("{price}", String(purchaseResult.price_paid))}
              </p>
              {(purchaseResult.seller_safe_zones || []).length > 0 ? (
                <div>
                  <p className={"text-xs uppercase tracking-widest text-neutral-500 font-body mb-2"}>
                    {t("market.suggested_safe_zones", "Suggested Safe Zones")}
                  </p>
                  <ul className="space-y-2">
                    {(purchaseResult.seller_safe_zones || []).map((z) => (
                      <li
                        key={z.id}
                        data-testid={`listing-purchase-safezone-${z.id}`}
                        className="rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white font-body flex items-center justify-between"
                      >
                        <span>{z.name}</span>
                        <span className="text-xs text-[#A1A1AA] flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {Math.round(z.distance_meters)}m
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <button
                type="button"
                data-testid="listing-purchase-open-chat"
                onClick={() => {
                  onClose();
                  navigate("/app/chat");
                }}
                className="mt-3 h-11 w-full rounded-md border border-[#E5B83A]/60 text-[#E5B83A] font-medium text-sm hover:bg-[#E5B83A]/10 transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                {t("market.open_chat_cta", "Open chat with seller")}
              </button>
            </div>
          ) : (
            <div className="px-5 py-5 space-y-4">
              {/* Photo carousel */}
              <div className="rounded-md overflow-hidden bg-black">
                {listing.photo_urls && listing.photo_urls.length > 0 ? (
                  <>
                    <img
                      src={listing.photo_urls[photoIdx] || listing.photo_urls[0]}
                      alt={`Sticker #${listing.number}`}
                      className="w-full max-h-[320px] object-contain bg-black"
                    />
                    {listing.photo_urls.length > 1 ? (
                      <div className="flex justify-center gap-2 py-2 bg-black/60">
                        {listing.photo_urls.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            aria-label={`Photo ${i + 1}`}
                            onClick={() => setPhotoIdx(i)}
                            className={
                              "h-2 w-2 rounded-full transition-all " +
                              (i === photoIdx
                                ? "bg-[#FFD700] w-4"
                                : "bg-white/30")
                            }
                          />
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="aspect-[4/3] flex items-center justify-center text-white/40 font-mono text-2xl">
                    #{listing.number}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span
                  className="font-display text-2xl font-semibold text-[#E5B83A]"
                  data-testid="listing-detail-price"
                >
                  {listing.price} FiguTokens
                </span>
                <span
                  className="text-xs uppercase tracking-widest text-[#FFD700]/80 font-body"
                  data-testid="listing-detail-grade"
                >
                  {listing.grade}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs text-[#A1A1AA] font-body">
                <span>
                  {t("market.seller_label", "Seller")}:{" "}
                  <span
                    className="text-white"
                    data-testid="listing-detail-seller"
                  >
                    {listing.seller_display_name || "—"}
                  </span>
                </span>
                {Number.isFinite(listing.distance_meters) ? (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {Math.round(listing.distance_meters)}m
                  </span>
                ) : null}
              </div>

              {listing.description ? (
                <p
                  className="text-sm text-white/80 font-body whitespace-pre-wrap"
                  data-testid="listing-detail-description"
                >
                  {listing.description}
                </p>
              ) : null}

              <p className="text-xs text-[#A1A1AA] font-body italic">
                {t(
                  "market.meetup_pre_purchase",
                  "Meetup coordinated via Safe Zone after purchase."
                )}
              </p>

              {error ? (
                <div
                  data-testid="listing-detail-error"
                  role="alert"
                  className="rounded-md border-2 border-[#FF0000] bg-[#3B0000]/40 px-3 py-2 text-sm text-[#FFAAAA] font-body"
                >
                  {error}
                </div>
              ) : null}

              {isSold || isCancelled || isExpired ? (
                <p
                  data-testid="listing-detail-closed"
                  className="text-xs text-[#FFB86B] font-body"
                >
                  {isSold
                    ? t("market.status_sold", "Sold.")
                    : isCancelled
                    ? t("market.status_cancelled", "Cancelled.")
                    : t("market.status_expired", "Expired.")}
                </p>
              ) : null}
            </div>
          )}
        </div>

        {listing && !purchaseResult && listing.status === "active" ? (
          <footer className="px-5 py-4 border-t border-white/10 flex items-center gap-3">
            {isSeller ? (
              <button
                type="button"
                onClick={onCancel}
                disabled={cancel.isPending}
                data-testid="listing-detail-cancel"
                className="flex-1 h-11 rounded-md border border-[#FF6B6B]/50 text-[#FF6B6B] font-medium text-sm hover:bg-[#FF6B6B]/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {cancel.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Ban className="h-4 w-4" />
                )}
                {t("market.cancel_listing", "Cancel listing")}
              </button>
            ) : (
              <button
                type="button"
                onClick={onBuy}
                disabled={!canAfford || purchase.isPending}
                title={
                  !canAfford
                    ? t(
                        "market.err_insufficient",
                        "Not enough FiguTokens — top up via the Wallet."
                      )
                    : ""
                }
                data-testid="listing-detail-buy"
                className="flex-1 h-11 rounded-md font-semibold text-sm bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black hover:opacity-95 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {purchase.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShoppingBag className="h-4 w-4" />
                )}
                {t("market.buy_for", "Buy for {price} FT").replace(
                  "{price}",
                  String(listing.price)
                )}
              </button>
            )}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
